import type { ThreadUpdateEvent } from "@/lib/use-visitor-threads-realtime";
import type { WidgetRoute } from "@/lib/widget-route";
import { isNewAlertSignature } from "./alert-dedupe";

/** What has been announced (sound / notification) in this mount. */
export interface AnnounceState {
  /** `lastMessageId`, or `threadId@lastMessageAt` on an older BE, of every announced message. */
  ids: Set<string>;
  /** Per thread, the `lastMessageAt` already accounted for (initial load or last announcement). */
  held: Record<string, string>;
  seeded: boolean;
}

export function createAnnounceState(): AnnounceState {
  return { ids: new Set(), held: {}, seeded: false };
}

/** Record the initial thread list: what the visitor already had never announces. */
export function seedAnnounceState(
  state: AnnounceState,
  threads: readonly { chatId: string; at?: string | null }[],
): void {
  for (const t of threads) state.held[t.chatId] = t.at ?? "";
  state.seeded = true;
}

/**
 * Should this `threadUpdated` frame sound / notify? Only a content frame for an inbound message (not the
 * visitor's own, not a BE thread seed) that has not been announced yet. An edit keeps `lastMessageId` and
 * `lastMessageAt`, so it never announces; neither does a reconnect / resync replay. Marks it announced.
 */
export function takeAnnouncement(
  state: AnnounceState,
  e: ThreadUpdateEvent,
  isSeedFrame: boolean,
): boolean {
  if (e.lastMessageFromVisitor !== false || isSeedFrame) return false;
  const preview = typeof e.lastMessagePreview === "string" ? e.lastMessagePreview.trim() : "";
  const sig = e.lastMessageAt ?? (preview !== "" ? preview : null);
  if (!e.lastMessageId && sig === null) return false;
  const key = e.lastMessageId ?? `${e.threadId}@${sig}`;
  if (state.ids.has(key)) return false;
  // A message at or before what we already hold (initial load, an earlier frame) is not new.
  if (e.lastMessageAt && !isNewAlertSignature(state.held[e.threadId], e.lastMessageAt)) {
    return false;
  }
  state.ids.add(key);
  if (e.lastMessageAt) state.held[e.threadId] = e.lastMessageAt;
  return true;
}

/**
 * Is the visitor reading THIS thread right now (panel open, tab visible, on that thread's conversation)?
 * Only then is the desktop notification redundant. The chatless anchor route is the session chat.
 */
export function isReadingThread(args: {
  isOpen: boolean;
  docVisible: boolean;
  route: WidgetRoute;
  threadId: string;
  anchorChatId: string | null | undefined;
}): boolean {
  const { isOpen, docVisible, route, threadId, anchorChatId } = args;
  if (!isOpen || !docVisible || route.kind !== "messages-thread") return false;
  const openChatId = route.chatId ?? (route.threadKey ? null : anchorChatId);
  return !!openChatId && openChatId === threadId;
}
